import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useNavigate } from 'react-router-dom';
import {
  financialCalculators,
  fitnessCalculators,
  mathCalculators,
  otherCalculators
} from '../data/mock';

const SearchBar = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [filteredResults, setFilteredResults] = useState([]);
  const wrapperRef = useRef(null);
  const navigate = useNavigate();

  // Combine all calculators - memoized to prevent recreation on every render
  const allCalculators = useMemo(() => [
    ...financialCalculators,
    ...fitnessCalculators,
    ...mathCalculators,
    ...otherCalculators
  ], []);

  // Filter calculators based on search query
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const results = allCalculators.filter(calc =>
        calc.name.toLowerCase().includes(query)
      );
      setFilteredResults(results);
      setShowResults(true);
    } else {
      setFilteredResults([]);
      setShowResults(false);
    }
  }, [searchQuery, allCalculators]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (filteredResults.length > 0) {
      navigate(filteredResults[0].path);
      setShowResults(false);
      setSearchQuery('');
    }
  };

  const handleResultClick = (path) => {
    navigate(path);
    setShowResults(false);
    setSearchQuery('');
  };

  return (
    <div ref={wrapperRef} className="relative">
      <form onSubmit={handleSearch} className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Input
            type="text"
            placeholder="Search calculators..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery && setShowResults(true)}
            className="bg-[#1a1a1a] border-[#2a2a2a] text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-cyan-500/20 pr-10 rounded-xl"
          />
        </div>
        <button
          type="submit"
          className="px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl hover:from-cyan-400 hover:to-blue-500 transition-all flex items-center gap-2 font-medium shadow-lg shadow-cyan-500/20"
        >
          <Search className="w-4 h-4" />
          Search
        </button>
      </form>

      {/* Search Results Dropdown */}
      {showResults && filteredResults.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
          {filteredResults.map((calc, index) => (
            <button
              key={index}
              onClick={() => handleResultClick(calc.path)}
              className="w-full px-4 py-3 text-left text-white hover:bg-[#2a2a2a] transition-colors border-b border-[#2a2a2a] last:border-b-0 flex items-center gap-2"
            >
              <Search className="w-4 h-4 text-cyan-400" />
              {calc.name}
            </button>
          ))}
        </div>
      )}

      {/* No Results */}
      {showResults && searchQuery && filteredResults.length === 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl shadow-xl z-50 p-4 text-gray-400 text-center">
          No calculators found for "{searchQuery}"
        </div>
      )}
    </div>
  );
};

export default SearchBar;
